import type { IdentityRequirement } from "./identity-assurance"

export type ComplianceBucket = "remoteEsign" | "inPersonOnly" | "wetSignFollowUp"
export type ComplianceMode = "interactiveRemoteEsign" | "remoteReviewFollowUp" | "inPersonOnly"

export interface ComplianceInput {
  esignAllowed: boolean
  requiresWetSignature?: boolean
  requiresNotary?: boolean
  identityRequirement?: IdentityRequirement
  notes?: string
}

export const COMPLIANCE_REVIEW_REQUIRED_NOTE_PREFIX = "[Compliance review required]"

export interface ComplianceEntryInput {
  documentName: string
  templateId: string
  dealPacketTemplateId: string
  compliance?: ComplianceInput
}

export interface ComplianceDocumentEntry {
  documentName: string
  templateId: string
  dealPacketTemplateId: string
  bucket: ComplianceBucket
  compliance: ComplianceInput | undefined
}

export interface PacketComplianceAnalysis {
  documents: ComplianceDocumentEntry[]
  remoteEsignCount: number
  inPersonOnlyCount: number
  wetSignFollowUpCount: number
  hasNonEsignable: boolean
  hasWetSign: boolean
  hasNotary: boolean
  complianceNotes: Array<{ templateId: string; documentName: string; note: string }>
  requiresAcknowledgement: boolean
}

export function classifyBucket(compliance: ComplianceInput | undefined): ComplianceBucket {
  if (!compliance) {
    return "inPersonOnly"
  }

  if (compliance.esignAllowed) {
    if (compliance.requiresWetSignature || compliance.requiresNotary) {
      return "wetSignFollowUp"
    }
    return "remoteEsign"
  }

  return "inPersonOnly"
}

export function getComplianceMode(compliance: ComplianceInput | undefined): ComplianceMode {
  const bucket = classifyBucket(compliance)

  switch (bucket) {
    case "remoteEsign":
      return "interactiveRemoteEsign"
    case "wetSignFollowUp":
      return "remoteReviewFollowUp"
    case "inPersonOnly":
      return "inPersonOnly"
  }
}

export function applyComplianceMode(
  mode: ComplianceMode,
  compliance: ComplianceInput | undefined,
): ComplianceInput {
  const next: ComplianceInput = {
    esignAllowed: mode !== "inPersonOnly",
    requiresWetSignature: compliance?.requiresWetSignature,
    requiresNotary: compliance?.requiresNotary,
    identityRequirement: compliance?.identityRequirement,
    notes: compliance?.notes,
  }

  if (mode === "interactiveRemoteEsign") {
    next.requiresWetSignature = undefined
    next.requiresNotary = undefined
    return next
  }

  if (
    mode === "remoteReviewFollowUp" &&
    next.requiresWetSignature !== true &&
    next.requiresNotary !== true
  ) {
    next.requiresWetSignature = true
  }

  return next
}

export function isComplianceReviewRequired(compliance: ComplianceInput | undefined) {
  if (!compliance) {
    return true
  }

  const notes = compliance.notes?.trim()
  return notes?.startsWith(COMPLIANCE_REVIEW_REQUIRED_NOTE_PREFIX) ?? false
}

export function analyzePacketCompliance(entries: ComplianceEntryInput[]): PacketComplianceAnalysis {
  const documents: ComplianceDocumentEntry[] = entries.map((entry) => ({
    documentName: entry.documentName,
    templateId: entry.templateId,
    dealPacketTemplateId: entry.dealPacketTemplateId,
    bucket: classifyBucket(entry.compliance),
    compliance: entry.compliance,
  }))

  const remoteEsignCount = documents.filter((d) => d.bucket === "remoteEsign").length
  const inPersonOnlyCount = documents.filter((d) => d.bucket === "inPersonOnly").length
  const wetSignFollowUpCount = documents.filter((d) => d.bucket === "wetSignFollowUp").length

  const hasNonEsignable = inPersonOnlyCount > 0
  const hasWetSign = documents.some((d) => d.compliance?.requiresWetSignature)
  const hasNotary = documents.some((d) => d.compliance?.requiresNotary)

  const complianceNotes = entries
    .filter((e) => e.compliance?.notes)
    .map((e) => ({
      templateId: e.templateId,
      documentName: e.documentName,
      note: e.compliance!.notes!,
    }))

  return {
    documents,
    remoteEsignCount,
    inPersonOnlyCount,
    wetSignFollowUpCount,
    hasNonEsignable,
    hasWetSign,
    hasNotary,
    complianceNotes,
    requiresAcknowledgement: hasNonEsignable || hasWetSign || hasNotary,
  }
}

// --- Follow-up tracking ---

export type FollowUpStatus = "pending" | "in_progress" | "completed"

export interface FollowUpEntry {
  dealPacketTemplateId: string
  templateId: string
  documentName: string
  bucket: ComplianceBucket
  status: FollowUpStatus
  assignedTo?: string
  note?: string
  lastUpdatedAt?: number
}

export interface FollowUpSummary {
  entries: FollowUpEntry[]
  pendingCount: number
  inProgressCount: number
  completedCount: number
  total: number
  allCompleted: boolean
}

export function isFollowUpBucket(bucket: ComplianceBucket): boolean {
  return bucket === "inPersonOnly" || bucket === "wetSignFollowUp"
}

export function isRemoteEsign(bucket: ComplianceBucket): boolean {
  return bucket === "remoteEsign"
}

export function toFollowUpInputs(
  followUps: Array<{
    dealPacketTemplateId: { toString(): string }
    status: FollowUpStatus
    assignedTo?: string
    note?: string
    updatedAt?: number
  }>,
) {
  return followUps.map((f) => ({
    dealPacketTemplateId: String(f.dealPacketTemplateId),
    status: f.status,
    assignedTo: f.assignedTo,
    note: f.note,
    lastUpdatedAt: f.updatedAt,
  }))
}

export function buildFollowUpSummary(
  complianceDocs: ComplianceDocumentEntry[],
  storedFollowUps: Array<{
    dealPacketTemplateId: string
    status: FollowUpStatus
    assignedTo?: string
    note?: string
    lastUpdatedAt?: number
  }>,
): FollowUpSummary {
  const storedByPtId = new Map(storedFollowUps.map((f) => [f.dealPacketTemplateId, f]))

  const entries: FollowUpEntry[] = complianceDocs
    .filter((doc) => isFollowUpBucket(doc.bucket))
    .map((doc) => {
      const ptId = doc.dealPacketTemplateId
      const stored = storedByPtId.get(ptId)
      return {
        dealPacketTemplateId: ptId,
        templateId: doc.templateId,
        documentName: doc.documentName,
        bucket: doc.bucket,
        status: stored?.status ?? "pending",
        assignedTo: stored?.assignedTo,
        note: stored?.note,
        lastUpdatedAt: stored?.lastUpdatedAt,
      }
    })

  const pendingCount = entries.filter((e) => e.status === "pending").length
  const inProgressCount = entries.filter((e) => e.status === "in_progress").length
  const completedCount = entries.filter((e) => e.status === "completed").length

  return {
    entries,
    pendingCount,
    inProgressCount,
    completedCount,
    total: entries.length,
    allCompleted: entries.length > 0 && completedCount === entries.length,
  }
}
